import { useAuth } from "./useAuth";
import { useCallback } from "react";

/**
 * EDrive API 연동 훅
 * - 파일/폴더 관리, 권한 관리, 검색 등 모든 기능 포함
 * - status: true/false 기반 성공여부
 * - denied, permission_denied 플래그 처리
 */
export default function useEdrive() {
  const { api } = useAuth();

  /** 권한 목록 조회 */
  const fetchPermissionList = useCallback(async () => {
    try {
      const { data } = await api.post("/edrive/permission/list");
      if (data?.denied) {
        return { ok: false, denied: true, permissions: [] };
      }
      if (!data?.status) {
        return { ok: false, denied: false, permissions: [] };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const permissions = raw.map((p) => ({
        enterprise_id: p.enterprise_id,
        user_id: p.user_id,
        user_name: p.user_name,
        access_level: p.access_level, // "browse_all" | "invited_only"
        granted_by: p.granted_by,
        granted_at: p.granted_at,
      }));
      return { ok: true, denied: false, permissions };
    } catch (e) {
      return { ok: false, denied: false, permissions: [] };
    }
  }, [api]);

  /** 권한 수정 (마스터만) */
  const updatePermission = useCallback(
    async (userId, changed) => {
      try {
        const { data } = await api.post("/edrive/permission/update", {
          user_id: userId,
          changed,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 폴더 생성 */
  const createFolder = useCallback(
    async ({ folder_name, parent_folder_id, folder_type, owner_id }) => {
      try {
        const { data } = await api.post("/edrive/create_folder", {
          folder_name,
          parent_folder_id: parent_folder_id || "",
          folder_type: folder_type || "private", // "private" | "share"
          owner_id: owner_id || "",
        });
        return !!data?.status;
      } catch (e) {
        return false;
      }
    },
    [api]
  );

  /** 폴더 수정 */
  const updateFolder = useCallback(
    async ({ folder_id, folder_name, owner_id }) => {
      try {
        const { data } = await api.post("/edrive/update_folder", {
          folder_id,
          folder_name,
          owner_id: owner_id || "",
        });
        return !!data?.status;
      } catch (e) {
        return false;
      }
    },
    [api]
  );

  /** 폴더 삭제 */
  const deleteFolder = useCallback(
    async (folderId) => {
      try {
        const { data } = await api.post("/edrive/delete_folder", {
          folder_id: folderId,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 루트 폴더 조회 */
  const fetchRootFolders = useCallback(async () => {
    try {
      const { data } = await api.get("/edrive/view_folder/root");
      if (!data?.status) {
        return { ok: false, folders: [] };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const folders = raw.map((f) => ({
        id: f.id,
        folder_name: f.folder_name,
        parent_folder_id: f.parent_folder_id,
        root_folder_id: f.root_folder_id,
        depth: f.depth,
        folder_type: f.folder_type,
        enterprise_id: f.enterprise_id,
        owner_id: f.owner_id,
        path: f.path,
        created_by: f.created_by,
        created_at: f.created_at,
        updated_at: f.updated_at,
      }));
      return { ok: true, folders };
    } catch (e) {
      return { ok: false, folders: [] };
    }
  }, [api]);

  /** 하위 폴더/파일 조회 */
  const fetchSubFolder = useCallback(
    async (folderId) => {
      try {
        const { data } = await api.get(
          `/edrive/view_folder/tree?fId=${folderId}`
        );
        if (data?.non_selector) {
          return { ok: true, nonSelector: true, folders: [], files: [] };
        }
        if (!data?.status) {
          return { ok: false, folders: [], files: [] };
        }
        const result = data.data || {};
        const folders = Array.isArray(result.folders)
          ? result.folders.map((f) => ({
              id: f.id,
              folder_name: f.folder_name,
              parent_folder_id: f.parent_folder_id,
              root_folder_id: f.root_folder_id,
              depth: f.depth,
              folder_type: f.folder_type,
              enterprise_id: f.enterprise_id,
              owner_id: f.owner_id,
              path: f.path,
              created_by: f.created_by,
              created_at: f.created_at,
              updated_at: f.updated_at,
            }))
          : [];
        const files = Array.isArray(result.files)
          ? result.files.map((f) => ({
              id: f.id,
              file_name: f.file_name,
              file_size: f.file_size,
              file_type: f.file_type,
              mime_type: f.mime_type,
              folder_id: f.folder_id,
              object_key: f.object_key,
              checksum: f.checksum,
              user_id: f.user_id,
              enterprise_id: f.enterprise_id,
              created_at: f.created_at,
              updated_at: f.updated_at,
            }))
          : [];
        return { ok: true, folders, files };
      } catch (e) {
        return { ok: false, folders: [], files: [] };
      }
    },
    [api]
  );

  /** 폴더 초대 */
  const inviteFolder = useCallback(
    async (inviteId, folderId) => {
      try {
        const { data } = await api.post("/edrive/folder/invite", {
          invite_id: inviteId,
          folder_id: folderId,
        });
        return !!data?.status;
      } catch (e) {
        return false;
      }
    },
    [api]
  );

  /** 폴더 추방 */
  const banFolder = useCallback(
    async (banId, folderId) => {
      try {
        const { data } = await api.post("/edrive/folder/ban", {
          ban_id: banId,
          folder_id: folderId,
        });
        return !!data?.status;
      } catch (e) {
        return false;
      }
    },
    [api]
  );

  /** 파일 업로드 */
  const uploadFiles = useCallback(
    async (files, folderId) => {
      try {
        const formData = new FormData();
        formData.append("folder_id", folderId || "");
        files.forEach((file) => {
          formData.append("files", file);
        });
        const { data } = await api.post("/edrive/file/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.empty) {
          return { ok: false, empty: true, failedFiles: [] };
        }
        if (!data?.status) {
          return { ok: false, empty: false, failedFiles: [] };
        }
        const result = data.data || {};
        return {
          ok: true,
          empty: false,
          failedFiles: result.failed_files || [],
          hasError: !result.occur_error,
        };
      } catch (e) {
        return { ok: false, empty: false, failedFiles: [] };
      }
    },
    [api]
  );

  /** 파일명 수정 */
  const renameFile = useCallback(
    async (fileId, newFileName) => {
      try {
        const { data } = await api.post("/edrive/file/rename", {
          file_id: fileId,
          new_file_name: newFileName,
        });
        return !!data?.status;
      } catch (e) {
        return false;
      }
    },
    [api]
  );

  /** 파일 삭제 */
  const deleteFile = useCallback(
    async (fileId) => {
      try {
        const { data } = await api.post("/edrive/file/delete", {
          file_id: fileId,
        });
        return {
          ok: !!data?.status,
          denied: !!data?.denied,
        };
      } catch (e) {
        return { ok: false, denied: false };
      }
    },
    [api]
  );

  /** 파일 뷰어 (API 호출) */
  const viewFile = useCallback(
    async (fileId) => {
      try {
        const response = await api.post(
          "/edrive/file/viewer",
          {
            file_id: fileId,
          },
          {
            responseType: "blob",
          }
        );
        return response.data;
      } catch (e) {
        console.error("파일 로드 실패:", e);
        return null;
      }
    },
    [api]
  );

  /** 파일 검색 */
  const searchFiles = useCallback(
    async (query) => {
      try {
        const { data } = await api.post(
          `/edrive/file/search?q=${encodeURIComponent(query)}`
        );
        if (!data?.status) {
          return { ok: false, result: { files: [], folders: [] } };
        }
        const result = data.data || {};
        return {
          ok: true,
          result: {
            files: Array.isArray(result.files) ? result.files : [],
            folders: Array.isArray(result.folders) ? result.folders : [],
            total_files: result.total_files || 0,
            total_folders: result.total_folders || 0,
          },
        };
      } catch (e) {
        return { ok: false, result: { files: [], folders: [] } };
      }
    },
    [api]
  );

  const fetchFolderInvitedMembers = useCallback(
    async (folderId) => {
      try {
        const { data } = await api.get(
          `/edrive/folder/invite/member?fId=${folderId}`
        );
        if (data?.non_selector) {
          return { ok: true, nonSelector: true, members: [] };
        }
        if (!data?.status) {
          return { ok: false, members: [] };
        }
        const raw = Array.isArray(data.data) ? data.data : [];
        const members = raw.map((m) => ({
          id: m.id,
          user_id: m.user_id,
          user_name: m.user_name,
          grade: m.grade,
          email: m.email,
          hp: m.hp,
          created_at: m.created_at,
          is_me: m.is_me,
        }));
        return { ok: true, members };
      } catch (e) {
        return { ok: false, members: [] };
      }
    },
    [api]
  );

  return {
    fetchPermissionList,
    updatePermission,
    createFolder,
    updateFolder,
    deleteFolder,
    fetchRootFolders,
    fetchSubFolder,
    inviteFolder,
    banFolder,
    uploadFiles,
    renameFile,
    deleteFile,
    viewFile,
    searchFiles,
    fetchFolderInvitedMembers,
  };
}
